/**
 * 移动端底部 TabBar：时间相册 / 那些话 / 我。
 *
 * 管理设置是 owner 专属的账号入口，不属于双人空间的主内容导航，
 * 因此不在底部 TabBar 中占用一个一级位置。
 */

import React from 'react';
import { useTheme, type ThemeId } from '../theme/theme';
import { AlbumIcon, LetterIcon, UserIcon, type IconProps } from './icons';
import { useIconStyle } from './iconStyle';

export type TabKey = 'album' | 'letters' | 'account';

export interface MobileTabBarProps {
  /** 当前激活 tab；null 表示停留在非 tab 页（如 /home） */
  active: TabKey | null;
  onChange: (next: TabKey) => void;
}

interface TabSpec {
  key: TabKey;
  label: string;
  testId: string;
}

const TAB_DEFS: TabSpec[] = [
  { key: 'album', label: '相册', testId: 'tab-album' },
  { key: 'letters', label: '那些话', testId: 'tab-letters' },
  { key: 'account', label: '我', testId: 'tab-account' },
];

function navIconPath(theme: ThemeId, name: TabKey, active: boolean): string {
  const themeName = theme === 'hand-drawn' ? 'hand-drawn' : theme;
  return `/generated-icons/nav-${name}-${themeName}-${active ? 'active' : 'inactive'}.png`;
}

const STANDARD_NAV_ICONS: Record<TabKey, React.ComponentType<IconProps>> = {
  album: AlbumIcon,
  letters: LetterIcon,
  account: UserIcon,
};

export const MobileTabBar: React.FC<MobileTabBarProps> = ({ active, onChange }) => {
  const visibleTabs = TAB_DEFS;
  const { theme } = useTheme();
  const { iconStyle } = useIconStyle();

  return (
    <nav
      className={`fp-mobile-tabbar fp-mobile-tabbar--count-${visibleTabs.length}`}
      aria-label="底部导航"
    >
      {visibleTabs.map((tab) => {
        const isActive = active === tab.key;
        return (
          <button
            key={tab.key}
            type="button"
            className={
              'fp-mobile-tabbar__item' + (isActive ? ' fp-mobile-tabbar__item--active' : '')
            }
            data-testid={tab.testId}
            onClick={() => onChange(tab.key)}
            aria-current={isActive ? 'page' : undefined}
          >
            <span className="fp-mobile-tabbar__icon-wrap" aria-hidden="true">
              {iconStyle === 'standard-svg' ? (
                React.createElement(STANDARD_NAV_ICONS[tab.key], {
                  className: 'fp-mobile-tabbar__icon',
                  size: 28,
                  'aria-hidden': true,
                })
              ) : (
                <img
                  className="fp-mobile-tabbar__icon"
                  src={navIconPath(theme, tab.key, isActive)}
                  alt=""
                />
              )}
            </span>
            <span>{tab.label}</span>
          </button>
        );
      })}
    </nav>
  );
};

export default MobileTabBar;
